import { existsSync, readdirSync, rmSync, writeFileSync, mkdirSync } from 'node:fs';
import { dirname, resolve } from 'node:path';
import { PROVIDERS } from '@/providers';
import { DRISHTI_API_KEY_NAME, SEARCH_PROVIDERS } from './env.js';
import { getMaxDir, maxPath } from './paths.js';

export type FactoryResetScope = 'all' | 'setup' | 'credentials';

export interface FactoryResetResult {
  deleted: string[];
}

const CREDENTIAL_HEADER = '# Max credentials — do not commit. Managed by the CLI.\n';

export const FACTORY_RESET_SCOPES: Record<FactoryResetScope, { title: string; description: string }> = {
  all: {
    title: 'All Max data',
    description: 'settings, API keys, cache, memory, history, rules, heartbeat, gateway config, and gateway credentials',
  },
  setup: {
    title: 'Setup only',
    description: 'settings and Max-managed API keys',
  },
  credentials: {
    title: 'Credentials only',
    description: 'Max-managed API keys',
  },
};

export function getMaxManagedApiKeyNames(): string[] {
  const names = new Set<string>();
  for (const provider of PROVIDERS) {
    if (provider.apiKeyEnvVar) names.add(provider.apiKeyEnvVar);
  }
  names.add(DRISHTI_API_KEY_NAME);
  for (const provider of Object.values(SEARCH_PROVIDERS)) {
    names.add(provider.apiKeyEnvVar);
  }
  return Array.from(names);
}

export function clearManagedApiKeysFromEnv(): void {
  for (const name of getMaxManagedApiKeyNames()) {
    delete process.env[name];
  }
}

export function assertSafeMaxDir(maxDir = getMaxDir()): string {
  if (!maxDir.trim()) {
    throw new Error('Refusing to reset unsafe Max home: <empty>');
  }
  const resolved = resolve(maxDir);
  if (!resolved || resolved === '/' || resolved.length < 6) {
    throw new Error(`Refusing to reset unsafe Max home: ${resolved || '<empty>'}`);
  }
  return resolved;
}

export function performFactoryReset(scope: FactoryResetScope): FactoryResetResult {
  const maxDir = assertSafeMaxDir();
  const deleted: string[] = [];

  if (scope === 'all') {
    if (existsSync(maxDir)) {
      for (const entry of readdirSync(maxDir)) {
        const path = maxPath(entry);
        rmSync(path, { recursive: true, force: true });
        deleted.push(entry);
      }
    }
    clearManagedApiKeysFromEnv();
    return { deleted };
  }

  const settingsPath = maxPath('settings.json');
  if (scope === 'setup' && existsSync(settingsPath)) {
    rmSync(settingsPath, { force: true });
    deleted.push('settings.json');
  }

  if (resetCredentialFile()) {
    deleted.push('credentials');
  }
  clearManagedApiKeysFromEnv();

  return { deleted };
}

function resetCredentialFile(): boolean {
  const path = maxPath('credentials');
  const dir = dirname(path);
  if (!existsSync(path)) return false;
  if (!existsSync(dir)) {
    mkdirSync(dir, { recursive: true, mode: 0o700 });
  }
  writeFileSync(path, CREDENTIAL_HEADER, { mode: 0o600 });
  return true;
}
